import { createContext, useContext, useEffect, useState } from "react";
import { Line, MaterialType, Province } from "../types/canvasTypes";

export const OptionContext = createContext<OptionContextType>({
  materialType: MaterialType.Silver,
  setMaterialType: () => {},
  province: Province.Quebec,
  setProvince: () => {},
  height: 42,
  setHeight: () => {},
  scale: 2.9,
  setScale: () => {},
  lines: [],
  setLines: () => {},
  chalkLine: false,
  setChalkLine: () => {},
  gapSize: 1,
  setGapSize: () => {},
});

interface OptionContextType {
  materialType: MaterialType;
  setMaterialType: (materialType: MaterialType) => void;
  province: Province;
  setProvince: (provinces: Province) => void;
  height: 36 | 42 | 48;
  setHeight: (height: 36 | 42 | 48) => void;
  scale: 2.9 | 1.5 | 1;
  setScale: (scale: 2.9 | 1.5 | 1) => void;
  lines: Line[];
  setLines: (lines: Line[]) => void;
  chalkLine: boolean;
  setChalkLine: (chalkLine: boolean) => void;
  gapSize: number;
  setGapSize: (gap: number) => void;
}

const heightStorageName = "RF-Height";
const scaleStorageName = "RF-Scale";
const provinceStorageName = "RF-Province";

export const OptionProvider = ({ children }: any) => {
  const [materialType, setMaterialType] = useState<MaterialType>(
    MaterialType.Silver
  );

  const heightFromStorage = (): 36 | 42 | 48 => {
    const storage = Number(localStorage.getItem(heightStorageName));
    if (36 === storage || 42 === storage || 48 === storage) {
      return storage;
    } else return 48;
  };

  const scaleFromStorage = (): 2.9 | 1.5 | 1 => {
    const storage = Number(localStorage.getItem(scaleStorageName));
    if (2.9 === storage || 1.5 === storage || 1 === storage) {
      return storage;
    } else return 2.9;
  };

  const [height, setHeight] = useState<36 | 42 | 48>(heightFromStorage());
  const [scale, setScale] = useState<2.9 | 1.5 | 1>(scaleFromStorage());
  const [province, setProvince] = useState<Province>(
    (localStorage.getItem(provinceStorageName) as Province) || Province.Quebec
  );
  const [lines, setLines] = useState<Line[]>([]);
  const [chalkLine, setChalkLine] = useState(false);
  const [gapSize, setGapSize] = useState(1);

  useEffect(() => {
    localStorage.setItem(heightStorageName, String(height));
  }, [height]);

  useEffect(() => {
    localStorage.setItem(scaleStorageName, String(scale));
  }, [scale]);

  useEffect(() => {
    localStorage.setItem(provinceStorageName, province);
    localStorage.setItem("lang", province === Province.Ontario ? "en" : "fr");
  }, [province]);

  return (
    <OptionContext.Provider
      value={{
        materialType,
        setMaterialType,
        province,
        setProvince,
        height,
        setHeight,
        scale,
        setScale,
        lines,
        setLines,
        chalkLine,
        setChalkLine,
        gapSize,
        setGapSize,
      }}
    >
      {children}
    </OptionContext.Provider>
  );
};

export const useOptionContext = () => useContext(OptionContext);
